import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import api, { formatApiError, formatCLP } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useConfig } from "@/contexts/ConfigContext";
import { BadgeCheck, MapPin, Star, ChevronLeft, Lock, ShieldCheck, Send, HeartHandshake, Building2, Video, Clock, Award } from "lucide-react";
import Avatar from "@/components/Avatar";

export default function MediatorDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { config } = useConfig();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [showV2, setShowV2] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await api.get(`/mediators/${id}`);
      setData(d);
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Auto-open solicitud form when ?solicitar=1 in URL (from directorio)
  useEffect(() => {
    if (searchParams.get("solicitar") === "1" && data?.mediator && user && (user.role === "lawyer" || user.role === "requester")) {
      if (config.new_mediation_enabled && data.mediator.mediator_directory_complete) {
        setShowV2(true);
      } else if (user.role === "lawyer") {
        setShowForm(true);
      }
    }
  }, [searchParams, data, user, config.new_mediation_enabled]);

  if (error) return <div className="container-app py-16 panel p-8 text-[hsl(var(--destructive))]">{error}</div>;
  if (!data) return <div className="container-app py-16 text-muted-foreground">Cargando…</div>;
  const m = data.mediator;

  return (
    <div className="container-app py-12" data-testid="mediator-detail">
      <button onClick={()=>nav(-1)} className="text-sm inline-flex items-center text-muted-foreground hover:text-foreground"><ChevronLeft size={14}/>Volver</button>

      <div className="mt-6 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="flex items-start gap-5 flex-wrap">
            <Avatar
              name={m.name}
              src={m.has_avatar ? m.avatar_url : null}
              size={88}
              testid="mediator-detail-avatar"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                {m.verified && <span className="pill badge-verified"><BadgeCheck size={12}/>Verificado</span>}
                {m.registry_number && <span className="pill">{m.registry_number}</span>}
                <span className="pill badge-cyan"><Lock size={12}/>Contacto reservado</span>
              </div>
              <h1 className="serif text-5xl mt-3">{m.name}</h1>
              <div className="mt-2 text-muted-foreground inline-flex items-center gap-1.5"><MapPin size={14}/>{m.comuna}, {m.region}</div>
            </div>
          </div>

          {/* Ficha destacada: Precio · Territorio · Modalidad — lo primero que se ve */}
          {m.verified && (m.mediator_tariff_totals || m.attends_presencial || m.attends_remote) && (
            <div className="mt-6 grid sm:grid-cols-3 gap-3" data-testid="mediator-hero-facts">
              {m.mediator_tariff_totals?.total_solicitante_frustrada_clp > 0 && (
                <div className="panel p-4 border-emerald-400/25">
                  <div className="label-eyebrow text-emerald-300">Precio mediación</div>
                  <div className="serif text-2xl mt-1">${m.mediator_tariff_totals.total_solicitante_frustrada_clp.toLocaleString("es-CL")}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">Total incluye todo</div>
                </div>
              )}
              <div className="panel p-4">
                <div className="label-eyebrow">Territorio</div>
                <div className="text-sm mt-1 font-medium">{m.comuna || "—"}</div>
                <div className="text-[11px] text-muted-foreground">{m.region || "—"}</div>
              </div>
              <div className="panel p-4">
                <div className="label-eyebrow">Modalidad</div>
                <div className="text-sm mt-1 font-medium">
                  {m.attends_presencial && m.attends_remote ? "Presencial y Telemática" :
                    m.attends_presencial ? "Presencial" :
                    m.attends_remote ? "Telemática" : "—"}
                </div>
              </div>
            </div>
          )}

          {m.bio && <p className="mt-6 text-foreground/90 leading-relaxed max-w-2xl">{m.bio}</p>}

          <div className="mt-6 flex flex-wrap gap-2">
            {(m.specialties || []).map((s) => <span key={s} className="pill">{s}</span>)}
          </div>

          {/* Modalidades de atención */}
          {(m.attends_presencial || m.attends_remote) && (
            <div className="mt-6" data-testid="mediator-modalities">
              <div className="label-eyebrow">Modalidades de atención</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {m.attends_presencial && m.attends_remote ? (
                  <span className="pill inline-flex items-center gap-1.5 border-cyan-400/40 bg-cyan-400/10 text-cyan-200" data-testid="modality-ambas">
                    <Building2 size={12}/> <Video size={12}/> Presencial y Telemática
                  </span>
                ) : (
                  <>
                    {m.attends_presencial && (
                      <span className="pill inline-flex items-center gap-1.5 border-cyan-400/40 bg-cyan-400/10 text-cyan-200" data-testid="modality-presencial">
                        <Building2 size={12}/> Presencial
                      </span>
                    )}
                    {m.attends_remote && (
                      <span className="pill inline-flex items-center gap-1.5 border-cyan-400/40 bg-cyan-400/10 text-cyan-200" data-testid="modality-remote">
                        <Video size={12}/> Telemática
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Contador de autoridad — solo visible con 3 o más mediaciones concluidas */}
          {(data.authority_counters?.mediations_completed || 0) >= 3 && (
            <div className="mt-6 panel-glow p-5 border-emerald-400/30" data-testid="mediator-authority-counter">
              <div className="flex items-center gap-3">
                <Award size={22} className="text-emerald-300 shrink-0"/>
                <div>
                  <div className="serif text-3xl leading-none" data-testid="mediator-mediations-count">
                    {data.authority_counters.mediations_completed}
                  </div>
                  <div className="text-sm text-emerald-200/90 mt-1">Mediaciones realizadas</div>
                </div>
              </div>
            </div>
          )}

          {/* Horarios y disponibilidad semanal (texto libre del mediador) */}
          {m.verified && m.horario_disponibilidad && (
            <div className="mt-8 panel p-5" data-testid="mediator-availability-block">
              <div className="label-eyebrow text-cyan-300 inline-flex items-center gap-1.5">
                <Clock size={12}/> Horarios y disponibilidad semanal
              </div>
              <p className="mt-2 text-sm whitespace-pre-line text-foreground/90" data-testid="mediator-availability-text">
                {m.horario_disponibilidad}
              </p>
            </div>
          )}

          {user && (user.role === "lawyer" || user.role === "requester") && !showForm && !showV2 && (
            <div className="mt-10">
              {m.verified && config.new_mediation_enabled && m.mediator_directory_complete ? (
                <button
                  onClick={() => setShowV2(true)}
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="open-request-v2"
                >
                  <Send size={16}/> Solicitar Mediación
                </button>
              ) : m.verified && user.role === "lawyer" ? (
                <button
                  onClick={() => setShowForm(true)}
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="open-request-mediation"
                >
                  <Send size={16}/> Solicitar Mediación
                </button>
              ) : !m.verified ? (
                <div className="panel p-5 text-sm text-muted-foreground max-w-md" data-testid="mediator-unverified-notice">
                  <ShieldCheck size={16} className="inline mr-2 text-amber-300"/>
                  Este mediador aún está en proceso de verificación por administración. Podrás solicitarle mediación una vez esté verificado.
                </div>
              ) : (
                <div className="panel p-5 text-sm text-muted-foreground max-w-md" data-testid="tariff-incomplete-notice">
                  <ShieldCheck size={16} className="inline mr-2 text-amber-300"/>
                  Este mediador aún no completa su tarifario público o modalidad de atención. Vuelve a intentarlo pronto o explora otros mediadores del Directorio.
                </div>
              )}
            </div>
          )}

          {showForm && user?.role === "lawyer" && (
            <div className="mt-10">
              <RequestForm mediator={m} onCreated={(id)=>nav(`/app/mediaciones/${id}`)} onCancel={()=>setShowForm(false)}/>
            </div>
          )}

          {showV2 && (
            <NewMediationRequestForm mediator={m} onCreated={(id)=>nav(`/app/mediacion-familiar/${id}`)} onCancel={()=>setShowV2(false)}/>
          )}

          <h2 className="serif text-3xl mt-12">Reseñas ({data.reviews.length})</h2>
          <ul className="mt-4 space-y-3" data-testid="reviews-list">
            {data.reviews.length === 0 && <li className="text-muted-foreground">Aún no hay reseñas.</li>}
            {data.reviews.map((r) => (
              <li key={r.id} className="panel p-5" data-testid={`review-${r.id}`}>
                <div className="flex items-center gap-3">
                  <span className="font-medium">{r.author_name}</span>
                  <span className="inline-flex items-center gap-1 text-sm"><Star size={14} className="text-[hsl(var(--primary))]"/>{r.rating}</span>
                </div>
                {r.comment && <p className="mt-2 text-sm text-foreground/85">{r.comment}</p>}
              </li>
            ))}
          </ul>
        </div>

        <aside className="space-y-4">
          <div className="panel p-6">
            <div className="label-eyebrow">Reputación</div>
            <div className="serif text-5xl mt-2 inline-flex items-center gap-2"><Star size={24} className="text-[hsl(var(--primary))]"/>{m.reputation || 0}</div>
            <div className="text-sm text-muted-foreground mt-1">{m.reviews_count || 0} reseñas</div>
          </div>

          <PublicTariffCard mediator={m}/>

          <div className="panel-glow p-6">
            <div className="flex items-center gap-2"><ShieldCheck size={16} className="text-[hsl(var(--accent))]"/><span className="label-eyebrow">Operación anónima</span></div>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
              No publicamos correos ni teléfonos. Toda la coordinación, archivos y pagos viajan dentro de Colega Link.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function RequestForm({ mediator, onCreated, onCancel }) {
  const [form, setForm] = useState({
    modality: "agreement",
    subject: "Pensión de alimentos",
    parties_description: "",
    notes: "",
    fee_clp: 120000,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/mediations", {
        mediator_id: mediator.id,
        modality: form.modality,
        subject: form.subject,
        parties_description: form.parties_description,
        notes: form.notes,
        fee_clp: Number(form.fee_clp),
      });
      onCreated(data.id);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="panel-glow p-7" data-testid="request-mediation-form">
      <div className="label-eyebrow text-[hsl(var(--primary))]">Nueva solicitud</div>
      <h3 className="serif text-2xl mt-1">Solicitar mediación a {mediator.name}</h3>
      <p className="text-sm text-muted-foreground mt-2">El mediador recibe la oferta y puede aceptar o rechazar. Si acepta, confirmas el Pago Protegido Colega Link.</p>

      <div className="mt-6 grid sm:grid-cols-2 gap-2">
        {[
          { v: "frustrated", t: "Mediación frustrada", d: "Pago al confirmar intento (independiente del acuerdo)" },
          { v: "agreement",  t: "Con acta de acuerdo", d: "Pago contra firma del acta de acuerdo" },
        ].map((opt) => (
          <button type="button" key={opt.v} onClick={()=>set("modality", opt.v)}
            className={`text-left panel p-4 transition-colors ${form.modality === opt.v ? "ring-1 ring-[hsl(var(--primary))]" : ""}`}
            data-testid={`modality-${opt.v}`}>
            <div className="font-medium">{opt.t}</div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{opt.d}</p>
          </button>
        ))}
      </div>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="label-eyebrow">Materia</span>
          <select className="field mt-1.5" value={form.subject} onChange={(e)=>set("subject", e.target.value)} data-testid="med-subject">
            {["Pensión de alimentos","Cuidado personal","Relación directa y regular","Régimen comunicacional","Compensación económica","Otra mediación familiar"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="label-eyebrow">Honorario propuesto (CLP)</span>
          <input className="field mt-1.5" type="number" min={0} step={10000} value={form.fee_clp} onChange={(e)=>set("fee_clp", e.target.value)} required data-testid="med-fee"/>
        </label>
        <label className="block sm:col-span-2">
          <span className="label-eyebrow">Partes (sin identificar a terceros si prefieres)</span>
          <input className="field mt-1.5" placeholder="Ej: Mi cliente vs. contraparte (sin nombres aún)" value={form.parties_description} onChange={(e)=>set("parties_description", e.target.value)} required data-testid="med-parties"/>
        </label>
        <label className="block sm:col-span-2">
          <span className="label-eyebrow">Notas para el mediador</span>
          <textarea className="field mt-1.5" rows={3} value={form.notes} onChange={(e)=>set("notes", e.target.value)} placeholder="Contexto breve, fechas disponibles, urgencia…" data-testid="med-notes"/>
        </label>
      </div>

      {err && <div className="mt-4 text-sm text-[hsl(var(--destructive))]" data-testid="med-error">{err}</div>}

      <div className="mt-6 flex gap-3 flex-wrap">
        <button disabled={busy} className="btn-primary" data-testid="med-submit">
          {busy ? "Enviando…" : "Enviar solicitud"}
        </button>
        <button type="button" onClick={onCancel} className="btn-ghost">Cancelar</button>
        <Link to="/app/mediaciones" className="ml-auto text-sm link-underline self-center">Mis mediaciones →</Link>
      </div>
    </form>
  );
}

function PublicTariffCard({ mediator }) {
  // El público/solicitante SÓLO ve totales finales (sin desglose de honorarios ni comisión ColegaLink)
  const totals = mediator.mediator_tariff_totals;
  if (!totals || !totals.total_solicitante_frustrada_clp) return null;
  const totalFrustrada = totals.total_solicitante_frustrada_clp;
  const totalAcuerdo = totals.total_solicitante_acuerdo_clp;
  const hasAcuerdo = totalAcuerdo > totalFrustrada;
  return (
    <div className="panel p-6" data-testid="public-tariff-card">
      <div className="label-eyebrow text-cyan-300">Tarifario</div>
      <ul className="mt-4 space-y-3 text-sm">
        <li className="flex justify-between border-b border-white/5 pb-3">
          <span className="text-muted-foreground">Precio mediación</span>
          <span className="font-medium text-lg" data-testid="pub-total-frustrada">{formatCLP(totalFrustrada)}</span>
        </li>
        {hasAcuerdo && (
          <li className="flex justify-between border-t border-white/5 pt-3">
            <span className="font-medium">Total si hay acuerdo</span>
            <span className="font-medium text-lg text-emerald-300" data-testid="pub-total-acuerdo">{formatCLP(totalAcuerdo)}</span>
          </li>
        )}
        <li className="text-[10px] text-muted-foreground pt-3 border-t border-white/5">
          Precios transparentes, sin sorpresas. Tope legal máximo total: <strong className="text-foreground">{formatCLP(130602)}</strong>.
        </li>
      </ul>
    </div>
  );
}


function NewMediationRequestForm({ mediator, onCreated, onCancel }) {
  const [form, setForm] = useState({ subject: "", notes: "", preferred_modality: "indistinto" });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const totals = mediator.mediator_tariff_totals || {};
  const totalFrustrada = totals.total_solicitante_frustrada_clp || 0;
  const totalAcuerdo = totals.total_solicitante_acuerdo_clp || 0;
  const hasAcuerdo = totalAcuerdo > totalFrustrada;

  const submit = async (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      setErr("Debes aceptar los Términos de Servicio para enviar la solicitud.");
      return;
    }
    setSaving(true); setErr("");
    try {
      const { data } = await api.post("/mediation-requests", {
        mediator_id: mediator.id,
        subject: form.subject.trim(),
        notes: form.notes.trim(),
        preferred_modality: form.preferred_modality,
      });
      onCreated(data.id);
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
    finally { setSaving(false); }
  };

  const supportsPresencial = mediator.attends_presencial;
  const supportsRemote = mediator.attends_remote;

  return (
    <form onSubmit={submit} className="panel p-6 mt-4 space-y-4" data-testid="new-mediation-request-form">
      <h3 className="serif text-2xl inline-flex items-center gap-2"><HeartHandshake size={18} className="text-cyan-300"/> Solicitud de Mediación Familiar Privada</h3>
      <div>
        <label className="text-xs label-eyebrow">Materia de la mediación *</label>
        <input required minLength={3} maxLength={140} className="field mt-1 w-full" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Ej: Pensión de alimentos, cuidado personal, régimen comunicacional…" data-testid="mr-subject"/>
      </div>
      <div>
        <label className="text-xs label-eyebrow">Breve resumen del caso *</label>
        <textarea required minLength={10} className="field mt-1 w-full" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Contexto general del conflicto y lo que buscas resolver. Evita datos personales sensibles." data-testid="mr-notes"/>
      </div>
      <div>
        <label className="text-xs label-eyebrow">Modalidad preferida</label>
        <div className="mt-2 grid sm:grid-cols-3 gap-2" data-testid="mr-modality-group">
          <ModalityOption
            active={form.preferred_modality === "presencial"}
            disabled={!supportsPresencial}
            icon={Building2}
            label="Presencial"
            hint={supportsPresencial ? "En oficina del mediador" : "No disponible"}
            onClick={() => supportsPresencial && setForm({ ...form, preferred_modality: "presencial" })}
            testid="mr-modality-presencial"
          />
          <ModalityOption
            active={form.preferred_modality === "remote"}
            disabled={!supportsRemote}
            icon={Video}
            label="Telemática"
            hint={supportsRemote ? "Videollamada" : "No disponible"}
            onClick={() => supportsRemote && setForm({ ...form, preferred_modality: "remote" })}
            testid="mr-modality-remote"
          />
          <ModalityOption
            active={form.preferred_modality === "indistinto"}
            icon={HeartHandshake}
            label="Indistinto"
            hint="Que decida el mediador"
            onClick={() => setForm({ ...form, preferred_modality: "indistinto" })}
            testid="mr-modality-indistinto"
          />
        </div>
      </div>

      <div className="p-4 rounded-md bg-cyan-400/5 border border-cyan-400/25 text-sm">
        <div className="label-eyebrow text-cyan-300">Resumen del precio</div>
        <ul className="mt-2 space-y-2 text-xs" data-testid="mr-tariff-summary">
          <li className="flex justify-between">
            <span className="font-medium">Monto inicial a pagar</span>
            <span className="font-medium text-cyan-200" data-testid="mr-pago-inicial">{formatCLP(totalFrustrada)}</span>
          </li>
          {hasAcuerdo && (
            <li className="flex justify-between border-t border-white/10 pt-2 mt-1">
              <span className="font-medium">Total final si hay acuerdo</span>
              <span className="font-medium text-emerald-300" data-testid="mr-total-acuerdo">{formatCLP(totalAcuerdo)}</span>
            </li>
          )}
        </ul>
        <p className="text-[10px] text-muted-foreground mt-2">Tope legal máximo total: {formatCLP(130602)}. El tarifario se congela cuando el mediador acepte tu solicitud. El botón de pago aparece tras la aceptación.</p>
      </div>

      {err && <div className="text-sm text-[hsl(var(--destructive))]" data-testid="mr-error">{err}</div>}
      <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="mr-terms-label">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.target.checked)}
          data-testid="mr-terms-checkbox"
        />
        <span>
          Acepto los{" "}
          <Link to="/terminos" target="_blank" className="underline">Términos de Servicio</Link>{" "}
          de ColegaLink, incluyendo la política de Pago Protegido, cancelaciones y confidencialidad de la mediación familiar privada.
        </span>
      </label>
      <div className="flex gap-2">
        <button type="submit" className="btn-primary flex-1 inline-flex items-center justify-center gap-2" disabled={saving || !termsAccepted} data-testid="mr-submit">
          {saving ? "Enviando…" : <><Send size={14}/> Enviar solicitud al mediador</>}
        </button>
        <button type="button" className="btn-ghost" onClick={onCancel} data-testid="mr-cancel">Cancelar</button>
      </div>
    </form>
  );
}

function ModalityOption({ active, disabled, icon: Icon, label, hint, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`panel p-3 text-left transition-colors ${
        active ? "ring-1 ring-cyan-400 border-cyan-400/40" : "border-white/10"
      } ${disabled ? "opacity-40 cursor-not-allowed" : "hover:border-cyan-400/40"}`}
    >
      <div className="flex items-center gap-2 text-sm font-medium">
        <Icon size={14} className={active ? "text-cyan-300" : "text-muted-foreground"}/> {label}
      </div>
      <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>
    </button>
  );
}

